// @ts-nocheck
import {
  afterProductAddToCart,
  cartFetchAndRender,
  setAddToCartButtonState,
} from '@theme/lib-cart';

/**
 * Handles logic for add-to-cart forms allowing reuse and encapsulation.
 */
class AddToCart {
  /** @type {WeakSet<HTMLFormElement>} */
  #inFlightForms = new WeakSet();

  constructor() {
    this._bindEvents();
  }

  _bindEvents() {
    document.addEventListener('click', (event) => this._onClick(event), true);

    document.addEventListener('submit', (event) => this._onSubmit(event), true);
  }

  _onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-add-to-cart-submit]');
    if (!(button instanceof HTMLButtonElement)) return;

    const form = button.form;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    this.addToCart(form, { button });
  }

  _onSubmit(event) {
    const form = this.getFormFromEvent(event.target);
    if (!(form instanceof HTMLFormElement)) return;

    if (!this.isAddToCartSubmit(event, form)) return;

    event.preventDefault();

    /** @type {HTMLButtonElement | undefined} */
    let button;
    if (event.submitter instanceof HTMLButtonElement) {
      const submitButton = event.submitter.closest('[data-add-to-cart-submit]');
      if (submitButton instanceof HTMLButtonElement) {
        button = submitButton;
      }
    }

    this.addToCart(form, { button });
  }

  /**
   * @param {Event} event
   * @param {HTMLFormElement} form
   */
  isAddToCartSubmit(event, form) {
    const submitter = event.submitter;
    if (submitter instanceof Element) {
      return Boolean(submitter.closest('[data-add-to-cart-submit]'));
    }

    const firstSubmit = form.querySelector(
      'button[type="submit"], input[type="submit"]'
    );
    return Boolean(firstSubmit?.closest('[data-add-to-cart-submit]'));
  }

  getFormFromEvent(target) {
    if (!(target instanceof Element)) return null;

    const form = target.closest('form');
    if (!(form instanceof HTMLFormElement)) return null;

    const button = form.querySelector('[data-add-to-cart-submit]');
    if (!button) return null;

    return form;
  }

  setMessage(form, text, isError) {
    const message = form.querySelector('[data-add-to-cart-message]');
    if (!message) return;

    if (!text) {
      message.textContent = '';
      message.setAttribute('hidden', 'hidden');
      message.classList.remove('is-error');
      return;
    }

    message.textContent = text;
    message.removeAttribute('hidden');
    message.classList.toggle('is-error', Boolean(isError));
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ button?: HTMLButtonElement; closeDialog?: boolean }} [options]
   */
  async addToCart(form, options = {}) {
    if (this.#inFlightForms.has(form)) return;

    const button =
      options.button ||
      form.querySelector('[data-add-to-cart-submit]') ||
      form.querySelector('button[type="submit"], input[type="submit"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const variantIdInput = form.querySelector("[name='id']");
    const variantId =
      button.dataset.variantId ||
      (variantIdInput instanceof HTMLInputElement ? variantIdInput.value : '');

    if (!variantId) {
      this.setMessage(
        form,
        Theme.translations.select_variant ||
          'Please select a valid product option.',
        true
      );
      return;
    }

    const quantityInput = form.querySelector("[name='quantity']");
    let quantity = 1;
    if (quantityInput instanceof HTMLInputElement && quantityInput.value) {
      const parsedQuantity = parseInt(quantityInput.value, 10);
      if (!Number.isNaN(parsedQuantity) && parsedQuantity > 0) {
        quantity = parsedQuantity;
      }
    }

    const fallbackErrorMessage =
      button.getAttribute('data-message-error') ||
      Theme.translations.add_to_cart_error ||
      'Unable to add this item right now.';

    this.#inFlightForms.add(form);

    this.setMessage(form, '');
    setAddToCartButtonState(button, 'loading');

    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
        },
      ],
    };

    const successMessage =
      button.getAttribute('data-message-success') ||
      Theme.translations.added_to_cart ||
      '';

    let didSetSuccessState = false;

    try {
      await cartFetchAndRender('cart/add.js', formData, {
        action: 'add',
        context: 'product',
        lines: [{ id: variantId, quantity }],
      });

      await afterProductAddToCart({
        origin: button,
        successMessage,
        closeDialog: options.closeDialog !== false,
      });

      if (successMessage) {
        setAddToCartButtonState(button, 'success', { message: successMessage });
        didSetSuccessState = true;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      const message =
        error instanceof Error && error.message
          ? error.message
          : fallbackErrorMessage;

      this.setMessage(form, message, true);
    } finally {
      if (!didSetSuccessState) {
        setAddToCartButtonState(button, 'idle');
      }
      this.#inFlightForms.delete(form);
    }
  }
}

const addToCartController = new AddToCart();

/**
 * @param {HTMLFormElement} form
 * @param {{ button?: HTMLButtonElement; closeDialog?: boolean }} [options]
 */
export function initProductFormAddToCart(form, options) {
  return addToCartController.addToCart(form, options);
}
